// A Hyperlink custom field's popover body on the Opportunities list. The rules are the record page's own
// (normalizeHyperlink), so the two surfaces cannot accept different links: https:// is added when the
// scheme is left out. Update or Enter in the box saves; Cancel and Escape discard. Saving an empty box
// clears the value. As on the record page, an invalid or too-long link says why only once the user tries
// to save it, so a link is not marked wrong while it is still being typed, and the reason clears on the
// next keystroke. The editable cell behind this popover is a button and cannot hold a link, so the stored
// link is opened from here.
import React, {useRef, useState} from 'react';
import {Button, EMPHASIS, TextField} from '@forecast-it/design-system';
import {CustomFieldHyperlink} from '../../../../../components/CustomFieldHyperlink';
import {normalizeHyperlink} from '../../../../shared/util/customFieldValueValidation';
import {Actions, Body} from './editorLayout';
import {saveCustomFieldValue} from './saveCustomFieldValue';
import type {CustomFieldEditorProps} from './CustomFieldEditor';

export const HyperlinkFieldEditor = ({node, def, current, onCommit, close, labels}: CustomFieldEditorProps): JSX.Element => {
	const initial = current ?? '';
	const [draft, setDraft] = useState(initial);
	// Set when a save is refused, cleared when the user types again.
	const [showProblem, setShowProblem] = useState(false);
	const result = normalizeHyperlink(draft);
	// Compare what would be saved, not what was typed: example.com over a stored https://example.com is the
	// same link, and saving it would commit a no-op mutation and refetch the whole list.
	const unchanged = result.valid ? (result.value ?? '') === initial : draft.trim() === initial.trim();
	// The popover stays mounted through its exit animation, so a second press could otherwise save again.
	const saving = useRef(false);
	const save = () => {
		if (unchanged || saving.current) return;
		if (!result.valid) {
			setShowProblem(true);
			return;
		}
		saving.current = true;
		saveCustomFieldValue({node, def, value: result.value, onCommit});
		close();
	};
	// The design-system TextField does not forward onKeyDown, so Enter is caught as it bubbles out of the
	// input. Only the input's own Enter saves, and not one that confirms an IME composition.
	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && e.target instanceof HTMLInputElement && !e.nativeEvent.isComposing) save();
	};
	const errorText = showProblem && !result.valid ? labels.hyperlinkProblems[result.reason] : undefined;
	return (
		<Body data-cy="cf-hyperlink-editor" onKeyDown={onKeyDown}>
			<TextField
				data-cy="cf-hyperlink-input"
				label={def.displayName}
				value={draft}
				onValueChange={(next: string) => {
					setDraft(next);
					setShowProblem(false);
				}}
				errorText={errorText}
			/>
			<CustomFieldHyperlink value={current} label={labels.openLink} data-cy="cf-hyperlink-open" />
			<Actions>
				<Button emphasis={EMPHASIS.LOW} data-cy="cf-cancel" onPress={close}>
					{labels.cancel}
				</Button>
				<Button emphasis={EMPHASIS.HIGH} data-cy="cf-submit" disabled={unchanged} onPress={save}>
					{labels.update}
				</Button>
			</Actions>
		</Body>
	);
};

export default HyperlinkFieldEditor;
